import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Group,
  Select,
  TextInput,
  NumberInput,
  Checkbox,
  Textarea,
  Text,
  Stack,
} from '@mantine/core';

const BOOKING_STATUS_OPTIONS = [
  { value: 'booked', label: 'Booked' },
  { value: 'inProcess', label: 'Request' },
  { value: 'closed', label: 'Closed' },
];

const BOOKING_SOURCE_OPTIONS = [
  { value: 'booking.com', label: 'Booking.com' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'vrbo', label: 'Vrbo' },
  { value: 'other', label: 'Other' },
];

const BookingEditModal = ({ opened, onClose, rangeKey, booking, onSave }) => {
  const [booked, setBooked] = useState('booked');
  const [bookingSource, setBookingSource] = useState('');
  const [customSource, setCustomSource] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState('');
  const [hasParking, setHasParking] = useState(false);
  const [hasDog, setHasDog] = useState(false);
  const [hasBaby, setHasBaby] = useState(false);
  const [numberOfDogs, setNumberOfDogs] = useState(0);
  const [breakfast, setBreakfast] = useState(false);
  const [specialWishes, setSpecialWishes] = useState('');
  const [underCityTaxAge, setUnderCityTaxAge] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!opened || !booking) return;
    const fd = booking.formDate || {};
    const src = (fd.bookingSource || '').trim();
    const known = ['booking.com', 'airbnb', 'vrbo'];
    if (known.includes(src)) {
      setBookingSource(src);
      setCustomSource('');
    } else {
      setBookingSource('other');
      setCustomSource(src);
    }
    setBooked(booking.booked || 'booked');
    setNumberOfGuests(
      fd.numberOfGuests !== undefined && fd.numberOfGuests !== null
        ? fd.numberOfGuests
        : ''
    );
    setHasParking(!!(fd.hasParking || fd.parking));
    setHasDog(!!(fd.hasDog || (Number(fd.numberOfDogs) > 0)));
    setHasBaby(!!(fd.hasBaby || fd.babycod));
    setNumberOfDogs(fd.numberOfDogs !== undefined && fd.numberOfDogs !== null ? fd.numberOfDogs : 0);
    setBreakfast(!!fd.breakfast);
    setSpecialWishes(fd.specialWishes ?? '');
    setUnderCityTaxAge(
      fd.underCityTaxAge !== undefined && fd.underCityTaxAge !== null
        ? fd.underCityTaxAge
        : ''
    );
    setEmail(fd.email ?? '');
    setMessage(fd.message ?? '');
  }, [opened, booking, rangeKey]);

  const handleSubmit = () => {
    if (!booking) return;
    const resolvedSource =
      bookingSource === 'other' ? (customSource || '').trim() : bookingSource;

    const guestsNum =
      numberOfGuests === '' || numberOfGuests === null || numberOfGuests === undefined
        ? 0
        : Number(numberOfGuests);

    const formDate = {
      ...(booking.formDate || {}),
      bookingSource: resolvedSource,
      numberOfGuests: guestsNum,
      hasParking,
      hasDog,
      hasBaby,
      numberOfDogs: Number(numberOfDogs) || 0,
      parking: hasParking,
      babycod: hasBaby,
      breakfast,
      specialWishes,
      underCityTaxAge:
        underCityTaxAge === '' || underCityTaxAge === null || underCityTaxAge === undefined
          ? 0
          : Number(underCityTaxAge),
      email: email.trim(),
      message,
    };

    onSave(rangeKey, {
      booked,
      label: booking.label || '',
      formDate,
    });
    onClose();
  };

  if (!booking) return null;

  return (
    <Modal opened={opened} onClose={onClose} title="Edit booking" size="lg">
      <Stack spacing="sm">
        <Text size="sm" color="dimmed">
          Check-in: {booking.start} — Check-out: {booking.end}
        </Text>

        <Select
          label="Status"
          data={BOOKING_STATUS_OPTIONS}
          value={booked}
          onChange={(v) => setBooked(v || 'booked')}
        />

        <Select
          label="Booking source"
          data={BOOKING_SOURCE_OPTIONS}
          placeholder="Select booking source"
          value={bookingSource || null}
          onChange={setBookingSource}
        />

        {bookingSource === 'other' && (
          <TextInput
            label="Custom source"
            placeholder="Enter custom booking source"
            value={customSource}
            onChange={(e) => setCustomSource(e.currentTarget.value)}
          />
        )}

        <NumberInput label="Number of guests" value={numberOfGuests} onChange={setNumberOfGuests} min={0} />

        <Group>
          <Checkbox label="Parking" checked={hasParking} onChange={(e) => setHasParking(e.currentTarget.checked)} />
          <Checkbox label="Dog" checked={hasDog} onChange={(e) => setHasDog(e.currentTarget.checked)} />
          <Checkbox label="Baby" checked={hasBaby} onChange={(e) => setHasBaby(e.currentTarget.checked)} />
        </Group>

        <NumberInput label="Number of dogs" value={numberOfDogs} onChange={setNumberOfDogs} min={0} />

        <Checkbox label="Breakfast" checked={breakfast} onChange={(e) => setBreakfast(e.currentTarget.checked)} />

        <NumberInput
          label="Guests under city tax age"
          value={underCityTaxAge}
          onChange={setUnderCityTaxAge}
          min={0}
        />

        <TextInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />

        <Textarea label="Message" value={message} onChange={(e) => setMessage(e.currentTarget.value)} minRows={2} />

        <Textarea
          label="Special wishes"
          placeholder="Add any special wishes or notes"
          value={specialWishes}
          onChange={(e) => setSpecialWishes(e.currentTarget.value)}
          minRows={2}
        />

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default BookingEditModal;
